import { Stack, Typography, useTheme } from "@mui/material";
import { Calendar, ChevronRight } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useFormatDateAndTimes } from "web_myplann/Activities/Hooks/useActivitiesFormatDateAndTimes";
import { DashboardRowButton } from "web_myplann/Dashboard/Components/DashboardRowButton";
import { useRelativeDays } from "web_myplann/Dashboard/Hooks/useRelativeDays";
import type { ActivityListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

type Props = {
	data: ActivityListItemResponseDTO;
	showDivider: boolean;
};

export const DashboardActivityRow: FC<Props> = ({ data, showDivider }) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const formatDateAndTimes = useFormatDateAndTimes();
	const relativeDays = useRelativeDays();

	const typeLabel = t(Resources.Activity.List.ActivityType[data.type]);
	const relativeLabel = relativeDays(data.date);
	const dateLabel = formatDateAndTimes(
		data.date ?? "",
		data.timeFrom ?? null,
		data.timeTo ?? null,
	);
	const place = data.place ?? "";

	return (
		<DashboardRowButton
			showDivider={showDivider}
			onClick={() => {
				if (data.id != null) {
					navigate(AppRouting.MyPlann.ActivityDetail.path(String(data.id)));
				}
			}}
		>
			<IconBox bg={theme.palette.green[100]}>
				<Calendar size={theme.iconSize.md} color={theme.palette.green[600]} />
			</IconBox>
			<Stack flex={1} minWidth={0}>
				<Typography
					variant="bodyStrong"
					color="text.primary"
					overflow="hidden"
					whiteSpace="nowrap"
					textOverflow="ellipsis"
				>
					{typeLabel}
				</Typography>
				{data.title && (
					<Typography
						variant="body2"
						color="text.secondary"
						overflow="hidden"
						whiteSpace="nowrap"
						textOverflow="ellipsis"
					>
						{data.title}
					</Typography>
				)}
				<Typography
					variant="caption"
					color={theme.palette.grey[500]}
					overflow="hidden"
					whiteSpace="nowrap"
					textOverflow="ellipsis"
				>
					{place ? `${dateLabel}, ${place}` : dateLabel}
				</Typography>
			</Stack>
			<Stack direction="row" alignItems="center" gap={0.4} flexShrink={0}>
				{relativeLabel && (
					<Typography variant="caption" color={theme.palette.grey[500]}>
						{relativeLabel}
					</Typography>
				)}
				<ChevronRight
					size={theme.iconSize.sm}
					color={theme.palette.grey[400]}
				/>
			</Stack>
		</DashboardRowButton>
	);
};
